import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Wallet, TrendingUp, TrendingDown, ArrowRight, Coins, Landmark, Mail, Layers, Banknote } from "lucide-react";
import { fmtDate } from "@/lib/format";

const KIND_ICON = { cash: Coins, bank: Landmark, postal: Mail, paypal: Banknote, stripe: Banknote, other: Wallet };

function euro(n) {
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);
}

export default function AdminDashboard() {
  const { data: summary } = useQuery({
    queryKey: ["admin-summary"],
    queryFn: async () => (await api.get("/administration/summary")).data,
  });
  const { data: movements = [] } = useQuery({
    queryKey: ["admin-movements-recent"],
    queryFn: async () => (await api.get("/administration/movements", { params: { limit: 10 } })).data,
  });

  const month = summary?.month;
  const saldoMese = useMemo(() => (month ? (month.entrata - month.uscita) : 0), [month]);

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-6" data-testid="admin-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-semibold text-[#1C352D]">Amministrazione</h1>
          <p className="text-sm text-stone-500 mt-1">Panoramica di entrate, uscite e saldi correnti.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/amministrazione/movimenti" className="text-xs bg-[#1C352D] text-white px-4 py-2 rounded-full hover:bg-[#264A3E]">+ Nuovo movimento</Link>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Kpi label="Saldo totale" value={euro(summary?.total_balance ?? 0)} icon={Wallet} color="#1C352D"/>
        <Kpi label="Entrate del mese" value={euro(month?.entrata ?? 0)} icon={TrendingUp} color="#16a34a"/>
        <Kpi label="Uscite del mese" value={euro(month?.uscita ?? 0)} icon={TrendingDown} color="#dc2626"/>
        <Kpi label="Saldo del mese" value={euro(saldoMese)} icon={Layers} color={saldoMese >= 0 ? "#16a34a" : "#dc2626"}/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Casse */}
        <Card className="lg:col-span-1 border border-stone-200 rounded-xl shadow-none">
          <div className="p-5 border-b border-stone-100 text-sm font-medium flex items-center justify-between">
            <span>Saldo per cassa</span>
            <Link to="/amministrazione/casse" className="text-xs text-stone-500 hover:text-[#1C352D]">Gestisci →</Link>
          </div>
          {(summary?.accounts || []).length === 0 ? (
            <div className="p-6 text-center text-stone-400 text-sm">Nessuna cassa configurata</div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {summary.accounts.map((a) => {
                const Icon = KIND_ICON[a.kind] || Wallet;
                return (
                  <li key={a.id} className="p-4 flex items-center gap-3" data-testid={`account-row-${a.id}`}>
                    <div className="w-9 h-9 rounded-lg grid place-items-center text-white shrink-0" style={{ background: a.color || "#1C352D" }}>
                      <Icon size={16}/>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{a.name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-stone-400">{a.kind}</div>
                    </div>
                    <div className={`text-sm font-semibold ${(a.balance || 0) >= 0 ? "text-[#1C352D]" : "text-red-700"}`}>{euro(a.balance)}</div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        {/* Ultimi movimenti */}
        <Card className="lg:col-span-2 border border-stone-200 rounded-xl shadow-none">
          <div className="p-5 border-b border-stone-100 text-sm font-medium flex items-center justify-between">
            <span>Ultimi movimenti</span>
            <Link to="/amministrazione/movimenti" className="text-xs text-stone-500 hover:text-[#1C352D] inline-flex items-center gap-1">Tutti <ArrowRight size={12}/></Link>
          </div>
          {movements.length === 0 ? (
            <div className="p-10 text-center text-stone-400 text-sm">Nessun movimento registrato</div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {movements.map((m) => (
                <li key={m.id} className="p-4 flex items-center gap-3">
                  <span className={`w-1.5 h-8 rounded-full shrink-0`} style={{ background: m.category_color || (m.type === "entrata" ? "#16a34a" : "#dc2626") }}/>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate flex items-center gap-2">
                      {m.description || m.category_name}
                      {m.attachment_url && <Badge className="bg-stone-100 text-stone-600 text-[9px] rounded-full border border-stone-200">📎</Badge>}
                    </div>
                    <div className="text-[11px] text-stone-500 mt-0.5">{fmtDate(m.date)} · {m.category_name} · {m.account_name}</div>
                  </div>
                  <div className={`text-sm font-semibold whitespace-nowrap ${m.type === "entrata" ? "text-emerald-700" : "text-red-700"}`}>
                    {m.type === "entrata" ? "+" : "−"} {euro(m.amount)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, color }) {
  return (
    <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg grid place-items-center text-white" style={{ background: color }}>
          <Icon size={18}/>
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
          <div className="text-2xl font-semibold text-[#1C352D] truncate">{value}</div>
        </div>
      </div>
    </Card>
  );
}
